/**
 * SF-4665: the two confirm dialogs of the fake data panel. Both read the remove impact live when
 * they open (loading is never shown as "nothing"), and show refusals as fixed copy only.
 */
import { useState } from 'react';
import { Alert, Button, Modal, Skeleton, Typography } from 'antd';
import {
  COUNT_LABELS,
  demoDataErrorMessage,
  removeAllArgs,
  removeBatchArgs,
  useBatchRemoveImpact,
  useCompanyRemoveImpact,
  useDemoDataMutation,
  type DemoBatch,
  type DemoDataCounts,
} from '../api/demoDataClient';
import { TypeNameToConfirm } from './TypeNameToConfirm';

const { Paragraph, Text } = Typography;

const ImpactCounts: React.FC<{ counts: DemoDataCounts; total: number; label: string }> = ({
  counts,
  total,
  label,
}) => {
  const rows = (Object.keys(COUNT_LABELS) as (keyof DemoDataCounts)[]).filter(
    (k) => (counts[k] ?? 0) > 0,
  );
  return (
    <>
      <Paragraph>
        <Text strong>{total}</Text> record{total === 1 ? '' : 's'} will be removed.
      </Paragraph>
      {rows.length > 0 ? (
        <ul aria-label={label} style={{ margin: '0 0 12px', paddingLeft: 20 }}>
          {rows.map((k) => (
            <li key={k}>
              {COUNT_LABELS[k]}: <Text strong>{counts[k]}</Text>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
};

/** A known refusal (e.g. BATCH_BUSY) gets its fixed copy; anything else the generic line. */
const LoadError: React.FC<{ error: unknown }> = ({ error }) => (
  <Alert
    type="error"
    showIcon
    message={demoDataErrorMessage(
      error,
      'Could not load what would be removed. Close and try again.',
    )}
  />
);

interface RemoveBatchModalProps {
  companyId: string;
  batch: DemoBatch;
  onClose: () => void;
  onRemoved: () => void;
}

/** Remove one batch. A legacy batch gets an extra warning; only it sends allow_legacy. */
export const RemoveBatchModal: React.FC<RemoveBatchModalProps> = ({
  companyId,
  batch,
  onClose,
  onRemoved,
}) => {
  const impactQuery = useBatchRemoveImpact(companyId, batch.id);
  const impact = impactQuery.data?.data;
  const remove = useDemoDataMutation();
  const [errorText, setErrorText] = useState<string | null>(null);

  // Counts still being re-read, or whose last re-read failed, are not shown as confirmed.
  const ready = !!impact && !impactQuery.isFetching && !impactQuery.isError;

  const confirm = () => {
    if (!ready || remove.isLoading) return;
    setErrorText(null);
    remove.mutate(removeBatchArgs(companyId, batch), {
      onSuccess: onRemoved,
      onError: (error) => setErrorText(demoDataErrorMessage(error)),
    });
  };

  return (
    <Modal
      title="Remove this fake data batch?"
      open
      onCancel={() => !remove.isLoading && onClose()}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose} disabled={remove.isLoading}>
          Cancel
        </Button>,
        <Button
          key="remove"
          danger
          type="primary"
          disabled={!ready}
          loading={remove.isLoading}
          onClick={confirm}
        >
          Remove batch
        </Button>,
      ]}
    >
      {batch.is_legacy ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="This is a legacy batch"
          description="It was added before fake data was tagged by batch, so it is matched by older rules. Check the counts carefully: this is the only place it can be removed."
        />
      ) : null}
      {impactQuery.isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : impactQuery.isError ? (
        <LoadError error={impactQuery.error} />
      ) : impact ? (
        <ImpactCounts counts={impact.counts} total={impact.total} label="Batch remove impact" />
      ) : null}
      {errorText ? <Alert type="error" showIcon style={{ marginTop: 12 }} message={errorText} /> : null}
    </Modal>
  );
};

interface RemoveAllModalProps {
  companyId: string;
  onClose: () => void;
  onRemoved: () => void;
}

/** Remove all non-legacy fake data. The company name must be typed exactly. */
export const RemoveAllModal: React.FC<RemoveAllModalProps> = ({ companyId, onClose, onRemoved }) => {
  const impactQuery = useCompanyRemoveImpact(companyId, true);
  const impact = impactQuery.data?.data;
  const remove = useDemoDataMutation();
  const [typedName, setTypedName] = useState('');
  const [errorText, setErrorText] = useState<string | null>(null);
  // Exact match against the name sf-api returned (sf-api re-checks it).
  const nameMatches = !!impact && typedName === impact.company.name;
  // A failed re-read keeps the old counts in the cache but shows an error: never confirmable.
  const ready = nameMatches && !impactQuery.isFetching && !impactQuery.isError;

  const confirm = () => {
    if (!ready || remove.isLoading) return;
    setErrorText(null);
    remove.mutate(removeAllArgs(companyId, typedName), {
      onSuccess: onRemoved,
      onError: (error) => setErrorText(demoDataErrorMessage(error)),
    });
  };

  return (
    <Modal
      title="Remove all fake data?"
      open
      onCancel={() => !remove.isLoading && onClose()}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose} disabled={remove.isLoading}>
          Cancel
        </Button>,
        <Button
          key="remove"
          danger
          type="primary"
          disabled={!ready}
          loading={remove.isLoading}
          onClick={confirm}
        >
          Remove all fake data
        </Button>,
      ]}
    >
      {impactQuery.isLoading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : impactQuery.isError ? (
        <LoadError error={impactQuery.error} />
      ) : impact ? (
        <>
          <ImpactCounts counts={impact.counts} total={impact.total} label="Company remove impact" />
          <Paragraph type="secondary">
            Legacy batches are kept
            {impact.legacy_batches_skipped > 0 ? ` (${impact.legacy_batches_skipped})` : ''}. Remove
            them one at a time from the list.
          </Paragraph>
          <TypeNameToConfirm
            name={impact.company.name}
            ariaLabel="Type the company name to confirm removing all fake data"
            value={typedName}
            onChange={(value) => {
              setTypedName(value);
              setErrorText(null);
            }}
            disabled={remove.isLoading}
          />
        </>
      ) : null}
      {errorText ? <Alert type="error" showIcon style={{ marginTop: 12 }} message={errorText} /> : null}
    </Modal>
  );
};
